<template>
  <div class="payment-form-wrapper">
    <header class="page-header fade-in">
      <button class="btn btn-light" @click="goBack">
        <i class="bi bi-arrow-left"></i>
        <span>Retour</span>
      </button>
      <div>
        <h1>Nouveau paiement</h1>
        <p>Enregistrez un paiement lié à une vente.</p>
      </div>
    </header>

    <section class="page-body fade-in">
      <form class="form-grid" @submit.prevent="handleSubmit">
        <div class="form-card">
          <h2>Détails du paiement</h2>

          <div class="form-group">
            <label>Client *</label>
            <button
              type="button"
              class="form-control customer-select-btn"
              :class="{
                'has-error': errors.seller_customer_reference,
                'has-selection': selectedCustomer,
              }"
              @click="showCustomerModal = true"
            >
              <span v-if="selectedCustomer">
                <i class="bi bi-person"></i>
                {{
                  selectedCustomer.fullName ||
                  selectedCustomer.name ||
                  selectedCustomer.society ||
                  selectedCustomer.reference
                }}
              </span>
              <span v-else class="placeholder">
                <i class="bi bi-person-plus"></i>
                Choisir un client
              </span>
              <i class="bi bi-chevron-down"></i>
            </button>
            <p v-if="errors.seller_customer_reference" class="error">
              {{ errors.seller_customer_reference }}
            </p>
          </div>

          <div class="form-group">
            <label>Vente *</label>
            <button
              type="button"
              class="form-control sale-select-btn"
              :class="{
                'has-error': errors.seller_sale_reference,
                'has-selection': selectedSale,
              }"
              @click="openSaleModal"
              :disabled="!selectedCustomer"
            >
              <span v-if="selectedSale">
                {{ selectedSale.reference }} — Total:
                {{ formatCurrency(selectedSale.total) }} — Restant:
                {{ formatCurrency(selectedSale.unpaidAmount) }}
              </span>
              <span v-else class="placeholder">
                {{
                  selectedCustomer
                    ? "Choisir une vente"
                    : "Sélectionnez d'abord un client"
                }}
              </span>
              <i class="bi bi-chevron-down"></i>
            </button>
            <p v-if="errors.seller_sale_reference" class="error">
              {{ errors.seller_sale_reference }}
            </p>
          </div>

          <div class="form-group">
            <label>Montant *</label>
            <div class="amount-input-wrapper">
              <input
                v-model.number="form.amount"
                type="number"
                min="0"
                step="0.01"
                class="form-control"
                :class="{ 'has-error': errors.amount }"
                placeholder="0.00"
              />
              <button
                v-if="selectedSale && selectedSale.unpaidAmount > 0"
                type="button"
                class="btn-amount-fill"
                @click="fillUnpaidAmount"
                title="Remplir avec le montant restant"
              >
                <i class="bi bi-arrow-down-circle"></i>
                <span>{{ formatCurrency(selectedSale.unpaidAmount) }}</span>
              </button>
            </div>
            <p v-if="errors.amount" class="error">{{ errors.amount }}</p>
            <p v-if="selectedSale && form.amount" class="amount-hint">
              <span
                v-if="Number(form.amount) > selectedSale.unpaidAmount"
                class="warning-text"
              >
                <i class="bi bi-exclamation-triangle"></i>
                Le montant dépasse le montant restant ({{
                  formatCurrency(selectedSale.unpaidAmount)
                }})
              </span>
              <span v-else class="info-text">
                Montant restant: {{ formatCurrency(selectedSale.unpaidAmount) }}
              </span>
            </p>
          </div>
        </div>

        <div class="form-card">
          <h2>Informations complémentaires</h2>
          <div class="form-group">
            <label>Date de paiement *</label>
            <input
              v-model="form.date"
              type="date"
              class="form-control"
              :class="{ 'has-error': errors.date }"
            />
            <p v-if="errors.date" class="error">
              {{ errors.date }}
            </p>
          </div>

          <div class="form-group">
            <label>Remarque</label>
            <textarea
              v-model.trim="form.remark"
              rows="4"
              class="form-control"
              placeholder="Commentaires, références bancaires..."
            ></textarea>
          </div>
        </div>

        <div class="form-actions">
          <button
            type="button"
            class="btn btn-light"
            @click="goBack"
            :disabled="submitting"
          >
            Annuler
          </button>
          <button type="submit" class="btn btn-primary" :disabled="submitting">
            <i class="bi bi-check-circle"></i>
            <span v-if="!submitting">Enregistrer</span>
            <span v-else>Enregistrement...</span>
          </button>
        </div>
      </form>
    </section>

    <!-- Customer Selection Modal -->
    <transition name="modal">
      <div
        v-if="showCustomerModal"
        class="modal-overlay"
        @click.self="showCustomerModal = false"
      >
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3 class="modal-title">Sélectionner un client</h3>
            <button
              class="btn-close"
              @click="showCustomerModal = false"
              aria-label="Fermer"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="modal-body">
            <div class="search-box" v-if="customers.length > 0">
              <i class="bi bi-search"></i>
              <input
                v-model="customerSearchTerm"
                type="text"
                placeholder="Rechercher un client..."
                class="search-input"
              />
            </div>

            <div v-if="filteredCustomers.length === 0" class="empty-state">
              <i class="bi bi-people"></i>
              <p v-if="customerSearchTerm">Aucun client trouvé</p>
              <p v-else>Aucun client disponible</p>
            </div>

            <div v-else class="customers-list">
              <button
                v-for="customer in filteredCustomers"
                :key="customer.reference || customer.id"
                class="customer-item"
                :class="{
                  active: form.seller_customer_reference === customer.reference,
                }"
                @click="selectCustomer(customer)"
              >
                <div class="customer-item-content">
                  <div class="customer-avatar">
                    {{ getCustomerInitials(customer) }}
                  </div>
                  <div class="customer-details">
                    <h4>
                      {{
                        customer.fullName ||
                        customer.name ||
                        customer.society ||
                        customer.reference
                      }}
                    </h4>
                    <div class="customer-meta">
                      <span v-if="customer.society" class="meta-item">
                        <i class="bi bi-building"></i>
                        {{ customer.society }}
                      </span>
                      <span v-if="customer.phone" class="meta-item">
                        <i class="bi bi-telephone"></i>
                        {{ customer.phone }}
                      </span>
                      <span
                        v-if="customer.city || customer.address"
                        class="meta-item"
                      >
                        <i class="bi bi-geo-alt"></i>
                        {{ customer.city || customer.address }}
                      </span>
                    </div>
                  </div>
                </div>
                <i
                  v-if="form.seller_customer_reference === customer.reference"
                  class="bi bi-check-circle-fill"
                ></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Sale Selection Modal -->
    <transition name="modal">
      <div
        v-if="showSaleModal"
        class="modal-overlay"
        @click.self="showSaleModal = false"
      >
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3 class="modal-title">Sélectionner une vente</h3>
            <button
              class="btn-close"
              @click="showSaleModal = false"
              aria-label="Fermer"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="modal-body">
            <div class="search-box" v-if="unpaidSales.length > 0">
              <i class="bi bi-search"></i>
              <input
                v-model="saleSearchTerm"
                type="text"
                placeholder="Rechercher une vente..."
                class="search-input"
              />
            </div>

            <div v-if="filteredSalesForModal.length === 0" class="empty-state">
              <i class="bi bi-inbox"></i>
              <p v-if="saleSearchTerm">Aucune vente trouvée</p>
              <p v-else>Aucune vente avec montant impayé</p>
            </div>

            <div v-else class="sales-list">
              <button
                v-for="sale in filteredSalesForModal"
                :key="sale.reference"
                class="sale-item"
                :class="{
                  active: form.seller_sale_reference === sale.reference,
                }"
                @click="selectSale(sale)"
              >
                <div class="sale-item-header">
                  <span class="sale-reference">{{ sale.reference }}</span>
                </div>
                <div class="sale-item-details">
                  <div class="sale-detail">
                    <span class="label">Total:</span>
                    <span class="value">{{ formatCurrency(sale.total) }}</span>
                  </div>
                  <div class="sale-detail">
                    <span class="label">Payé:</span>
                    <span class="value">{{
                      formatCurrency(sale.amountPaid)
                    }}</span>
                  </div>
                  <div class="sale-detail highlight">
                    <span class="label">Restant:</span>
                    <span class="value">{{
                      formatCurrency(sale.unpaidAmount)
                    }}</span>
                  </div>
                </div>
                <i
                  v-if="form.seller_sale_reference === sale.reference"
                  class="bi bi-check-circle-fill"
                ></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "PaymentCreate",
  data() {
    const today = new Date().toISOString().split("T")[0];
    return {
      submitting: false,
      errors: {},
      showCustomerModal: false,
      showSaleModal: false,
      customerSearchTerm: "",
      saleSearchTerm: "",
      form: {
        customer_reference: "",
        sale_reference: "",
        amount: "",
        date: today,
        remark: "",
      },
    };
  },
  computed: {
    ...mapGetters("sale", {
      sales: "getAll",
    }),
    ...mapGetters("customer", {
      customers: "getAll",
    }),
    normalizedSales() {
      const toNumber = (value, fallback = 0) => {
        if (value === null || value === undefined || value === "") {
          return fallback;
        }
        const number = Number(value);
        return Number.isNaN(number) ? fallback : number;
      };
      const rawSales = Array.isArray(this.sales?.data)
        ? this.sales.data
        : Array.isArray(this.sales)
        ? this.sales
        : [];

      return rawSales.map((entry) => {
        const reference =
          entry.reference || entry.sale_reference || entry.id || "";
        const total = toNumber(
          entry.totalPriceTTC != null
            ? entry.totalPriceTTC
            : entry.total_price_ttc != null
            ? entry.total_price_ttc
            : entry.total_amount != null
            ? entry.total_amount
            : entry.total,
          0
        );
        const amountPaid = toNumber(
          entry.amountPaid != null
            ? entry.amountPaid
            : entry.amount_paid != null
            ? entry.amount_paid
            : entry.payrollAmount,
          0
        );
        const unpaidAmount = toNumber(
          entry.amountUnpaid != null
            ? entry.amountUnpaid
            : entry.amount_unpaid != null
            ? entry.amount_unpaid
            : entry.remaining_amount != null
            ? entry.remaining_amount
            : total - amountPaid,
          0
        );
        const customer_reference =
          entry.seller_customer_reference ||
          entry.customer_reference ||
          entry.customer?.reference ||
          null;

        return {
          id: entry.id || entry.uuid || reference,
          reference,
          customer_reference,
          total,
          amountPaid,
          unpaidAmount,
        };
      });
    },
    unpaidSales() {
      let sales = this.normalizedSales.filter((sale) => sale.unpaidAmount > 0);
      // Filter by selected customer if available
      if (this.form.seller_customer_reference) {
        sales = sales.filter(
          (sale) =>
            sale.customer_reference === this.form.seller_customer_reference
        );
      }
      return sales;
    },
    selectedCustomer() {
      if (!this.form.seller_customer_reference) return null;
      return this.normalizedCustomers.find(
        (customer) => customer.reference === this.form.seller_customer_reference
      );
    },
    selectedSale() {
      if (!this.form.seller_sale_reference) return null;
      return this.unpaidSales.find(
        (sale) => sale.reference === this.form.seller_sale_reference
      );
    },
    normalizedCustomers() {
      const source = Array.isArray(this.customers?.data)
        ? this.customers.data
        : Array.isArray(this.customers)
        ? this.customers
        : [];
      return source.map((entry) => {
        const reference =
          entry.reference || entry.customer_reference || entry.id || "";
        return {
          id: entry.id || entry.uuid || reference,
          reference,
          fullName:
            entry.fullName ||
            entry.full_name ||
            entry.name ||
            entry.customer?.fullName ||
            entry.customer?.name ||
            null,
          name: entry.name || entry.customer?.name || null,
          society: entry.society || entry.customer?.society || null,
          phone: entry.phone || entry.customer?.phone || null,
          city: entry.city || entry.customer?.city || null,
          address: entry.address || entry.customer?.address || null,
          raw: entry,
        };
      });
    },
    filteredCustomers() {
      let customers = this.normalizedCustomers;
      if (this.customerSearchTerm) {
        const term = this.customerSearchTerm.toLowerCase();
        customers = customers.filter((customer) =>
          [
            customer.fullName,
            customer.name,
            customer.society,
            customer.phone,
            customer.city,
            customer.address,
            customer.reference,
          ]
            .filter(Boolean)
            .map((value) => value.toString().toLowerCase())
            .some((value) => value.includes(term))
        );
      }
      return customers;
    },
    filteredSalesForModal() {
      let sales = this.unpaidSales;
      if (this.saleSearchTerm) {
        const term = this.saleSearchTerm.toLowerCase();
        sales = sales.filter((sale) =>
          sale.reference.toLowerCase().includes(term)
        );
      }
      return sales;
    },
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    formatCurrency(amount) {
      if (!amount && amount !== 0) return "—";
      const number = Number(amount);
      if (Number.isNaN(number)) {
        return amount;
      }
      return number.toLocaleString("fr-FR", {
        style: "currency",
        currency: "MAD",
      });
    },
    validate() {
      const errors = {};
      if (!this.form.seller_customer_reference) {
        errors.seller_customer_reference = "Veuillez sélectionner un client.";
      }
      if (!this.form.seller_sale_reference) {
        errors.seller_sale_reference = "Veuillez sélectionner une vente.";
      }
      if (this.form.amount === "" || this.form.amount === null) {
        errors.amount = "Indiquez le montant du paiement.";
      } else if (Number(this.form.amount) <= 0) {
        errors.amount = "Le montant doit être supérieur à 0.";
      } else if (
        this.selectedSale &&
        Number(this.form.amount) > this.selectedSale.unpaidAmount
      ) {
        errors.amount = `Le montant ne peut pas dépasser le montant restant (${this.formatCurrency(
          this.selectedSale.unpaidAmount
        )}).`;
      }
      if (!this.form.date) {
        errors.date = "Sélectionnez la date du paiement.";
      }
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    fillUnpaidAmount() {
      if (this.selectedSale && this.selectedSale.unpaidAmount > 0) {
        this.form.amount = Number(this.selectedSale.unpaidAmount.toFixed(2));
      }
    },
    selectCustomer(customer) {
      this.form.seller_customer_reference = customer.reference;
      this.form.seller_sale_reference = ""; // Clear sale selection when customer changes
      this.showCustomerModal = false;
      this.customerSearchTerm = "";
    },
    openSaleModal() {
      if (!this.selectedCustomer) {
        this.errors.seller_customer_reference =
          "Veuillez d'abord sélectionner un client.";
        return;
      }
      this.showSaleModal = true;
    },
    selectSale(sale) {
      this.form.seller_sale_reference = sale.reference;
      this.showSaleModal = false;
      this.saleSearchTerm = "";
    },
    getCustomerInitials(customer) {
      const name = customer.fullName || customer.name || customer.society || "";
      if (!name) return "?";
      const parts = name.trim().split(/\s+/);
      const initials = [parts[0], parts[parts.length - 1]]
        .filter(Boolean)
        .map((part) => part[0]?.toUpperCase())
        .join("");
      return initials || name.charAt(0).toUpperCase();
    },
    async handleSubmit() {
      if (!this.validate()) return;
      this.submitting = true;
      try {
        // Get the selected sale to extract seller_reference

        // Get user reference from auth

        // Build payload with all required fields
        const payload = {
          sale_reference: this.form.sale_reference,
          amount: Number(this.form.amount),
          date: this.form.date,
          remark: this.form.remark || null,
        };

        await this.$store.dispatch("payment/store", payload);
        this.$toast.success("Paiement enregistré avec succès.");
        this.$router.push({ name: "payments-list" });
      } catch (error) {
        console.error("Error creating payment", error);
        this.$toast.error("Impossible d'enregistrer le paiement.");
      } finally {
        this.submitting = false;
      }
    },
  },
  async mounted() {
    try {
      await Promise.all([
        this.$store.dispatch("sale/getAll"),
        this.$store.dispatch("customer/getAll"),
      ]);
    } catch (error) {
      console.error("Error loading data", error);
      this.$toast.error("Impossible de charger les données.");
    }
  },
};
</script>

<style scoped>
.payment-form-wrapper {
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f8fb 0%, #f0f2f8 100%);
  padding: 2rem 2.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.page-header h1 {
  margin: 0 0 0.35rem;
  font-size: 2.15rem;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  color: #556073;
}

.page-body {
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.08);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;
}

.form-card {
  background: #f9f9ff;
  border-radius: 20px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: 0 12px 24px rgba(99, 102, 241, 0.08);
}

.form-card h2 {
  margin: 0;
  font-size: 1.3rem;
  color: #1f2937;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

label {
  font-weight: 600;
  color: #475569;
}

.form-control {
  border-radius: 12px;
  border: 1px solid #d1d5db;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.has-error {
  border-color: #ef4444;
}

.error {
  color: #ef4444;
  font-size: 0.85rem;
}

.form-actions {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-radius: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn-light {
  background: rgba(148, 163, 184, 0.15);
  color: #1f2937;
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  box-shadow: 0 10px 22px rgba(79, 70, 229, 0.25);
}

@media (max-width: 1024px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .payment-form-wrapper {
    padding: 1.25rem 1rem 4rem;
  }
  .page-body {
    padding: 1.5rem;
  }
  .form-card {
    padding: 1.25rem;
  }
  .form-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
  .btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.75rem;
  }
}

/* Sale Select Button */
.sale-select-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  text-align: left;
  background: white;
}

.sale-select-btn .placeholder {
  color: #9ca3af;
}

.sale-select-btn.has-selection {
  color: #1f2937;
}

.sale-select-btn i,
.customer-select-btn i {
  color: #6b7280;
  font-size: 1.1rem;
  transition: transform 0.2s ease;
}

.sale-select-btn:hover i,
.customer-select-btn:hover i {
  transform: translateY(2px);
}

.customer-select-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  text-align: left;
  background: white;
}

.customer-select-btn .placeholder {
  color: #9ca3af;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.customer-select-btn.has-selection {
  color: #1f2937;
}

.customer-select-btn.has-selection span {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.customer-select-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.amount-input-wrapper {
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
}

.amount-input-wrapper .form-control {
  flex: 1;
}

.btn-amount-fill {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  border: 1px solid #6366f1;
  background: linear-gradient(135deg, #6366f1, #4f46e5);
  color: white;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.btn-amount-fill:hover {
  background: linear-gradient(135deg, #4f46e5, #4338ca);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.btn-amount-fill i {
  font-size: 1rem;
}

.amount-hint {
  margin: 0.25rem 0 0;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.warning-text {
  color: #ef4444;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.warning-text i {
  font-size: 0.9rem;
}

.info-text {
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.2);
  width: 100%;
  max-width: 600px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.btn-close {
  background: transparent;
  border: none;
  color: #6b7280;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background: #f3f4f6;
  color: #1f2937;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
}

.search-box i {
  color: #6b7280;
  font-size: 1.1rem;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 1rem;
  color: #1f2937;
}

.search-input::placeholder {
  color: #9ca3af;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: #6b7280;
  text-align: center;
}

.empty-state i {
  font-size: 3rem;
  color: #d1d5db;
  margin-bottom: 1rem;
}

.empty-state p {
  margin: 0;
  font-size: 1rem;
}

.sales-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 400px;
  overflow-y: auto;
}

.sale-item {
  position: relative;
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.sale-item:hover {
  border-color: #6366f1;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
  transform: translateY(-2px);
}

.sale-item.active {
  border-color: #6366f1;
  background: #f0f4ff;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
}

.sale-item i.bi-check-circle-fill,
.customer-item i.bi-check-circle-fill {
  position: absolute;
  top: 1rem;
  right: 1rem;
  color: #6366f1;
  font-size: 1.25rem;
}

.customers-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 400px;
  overflow-y: auto;
}

.customer-item {
  position: relative;
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.customer-item:hover {
  border-color: #6366f1;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
  transform: translateY(-2px);
}

.customer-item.active {
  border-color: #6366f1;
  background: #f0f4ff;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
}

.customer-item-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;
}

.customer-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  flex-shrink: 0;
}

.customer-details {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
  flex: 1;
}

.customer-details h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
}

.customer-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.85rem;
  color: #64748b;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.meta-item i {
  font-size: 0.9rem;
  color: #94a3b8;
}

.sale-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sale-reference {
  font-weight: 700;
  font-size: 1.1rem;
  color: #0f172a;
}

.sale-item-details {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
}

.sale-detail {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.sale-detail .label {
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
}

.sale-detail .value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
}

.sale-detail.highlight .value {
  color: #6366f1;
  font-size: 1rem;
}

/* Modal Transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter .modal-content,
.modal-leave-to .modal-content {
  transform: translateY(-20px) scale(0.95);
  opacity: 0;
}

@media (max-width: 768px) {
  .modal-content {
    max-width: 100%;
    max-height: 90vh;
    border-radius: 20px 20px 0 0;
  }

  .sale-item-details {
    grid-template-columns: 1fr;
  }
}
</style>
